import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { ReactEditor, useSlate } from 'slate-react';
import { BaseRange } from 'slate';
import Portal from 'components/Portal';
import { TextHighlighter } from 'components/TextHighlighter';

import './TagOptions.styles.scss';

export type TagOption = { id: string; display: string; isRequired?: boolean };

const PADDING = 8;

type TagOptionsProps = {
  className?: string;
  searchString?: string;
  target?: BaseRange;
  selectedIndex?: number;
  options: TagOption[];
  closeTagOptions: () => void;
  onTagOptionSelect: (option: TagOption) => void;
};

const TagOptions: React.FC<TagOptionsProps> = (props) => {
  const {
    className,
    searchString,
    options,
    target,
    selectedIndex,
    closeTagOptions,
    onTagOptionSelect,
  } = props;

  const classes = classNames('tag-options', className);

  const contentRef = useRef<HTMLDivElement>();

  const editor = useSlate();

  const processDisplayText = useCallback(
    (text: string) => {
      if (!searchString) return text;

      return <TextHighlighter text={text} highlighter={searchString} />;
    },
    [searchString],
  );

  useEffect(() => {
    const handleClickedOutside = (event: MouseEvent) => {
      if (!contentRef.current?.contains(event.target as Node))
        closeTagOptions();
    };

    window.addEventListener('click', handleClickedOutside);

    return () => window.removeEventListener('click', handleClickedOutside);
  }, [closeTagOptions]);

  useEffect(() => {
    const el = contentRef?.current;

    if (options.length > 0 && el && target) {
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();

      const dropdownWidth = el.offsetWidth;
      const dropdownHeight = el.offsetHeight;

      const spaceRight = window.innerWidth - rect.left;
      const spaceBelow = window.innerHeight - rect.bottom;

      let top, left;

      // Vertical positioning
      if (spaceBelow >= dropdownHeight + PADDING) {
        top = rect.bottom + window.pageYOffset + PADDING;
      } else {
        top = rect.top + window.pageYOffset - dropdownHeight - PADDING;
      }

      // Horizontal positioning
      if (spaceRight >= dropdownWidth + PADDING) {
        left = rect.left + window.pageXOffset;
      } else {
        left = rect.right + window.pageXOffset - dropdownWidth;
      }

      el.style.top = `${top}px`;
      el.style.left = `${left}px`;
    }
  }, [options.length, editor, searchString, target]);

  useEffect(() => {
    const content = contentRef?.current;

    if (content) {
      const selectedChild = content.children[selectedIndex];

      if (selectedChild) {
        const parentRect = content.getBoundingClientRect();
        const childRect = selectedChild.getBoundingClientRect();

        if (
          childRect.top < parentRect.top ||
          childRect.bottom > parentRect.bottom
        ) {
          content.scrollTop += childRect.top - parentRect.top;
        }
      }
    }
  }, [selectedIndex]);

  return (
    <Portal isOpen={!!target}>
      <div className={classes} ref={contentRef}>
        {options.map((option, index) => (
          <div
            key={option.id}
            className={classNames('tag-options__option', {
              'tag-options__option--selected': selectedIndex === index,
            })}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onTagOptionSelect(option);
            }}
          >
            {processDisplayText(option.display)}
          </div>
        ))}
      </div>
    </Portal>
  );
};

export default TagOptions;
